import api from "./api";

/** What upgrading off the trial costs, and whether it can happen unattended. */
export type ActivationQuote = {
  club_id: string;
  club_name: string;
  plan_code: string;
  plan_name: string;
  currency: string;
  price_per_athlete_cents: number;
  athlete_count: number;
  monthly_estimate_cents: number;
  first_period: {
    starts_at: string;
    ends_at: string;
    total_days: number;
    billed_days: number;
    estimate_cents: number;
  };
  already_active: boolean;
  contract_ready: boolean;
  missing_legal_fields: { key: string; label: string }[];
  can_activate: boolean;
};

export type ClubContract = {
  id: string;
  number: string;
  title: string;
  status: string;
  accepted_at: string | null;
  sent_at: string | null;
  created_at: string;
};

export async function getActivationQuote(clubId: string): Promise<ActivationQuote> {
  const { data } = await api.get<ActivationQuote>(`/plan-activation/${clubId}/quote`);
  return data;
}

/**
 * The contract as a PDF blob, so it can be shown in the page rather than
 * downloaded and opened elsewhere. Nobody reads a contract they had to go
 * and find in their Downloads folder.
 */
export async function getActivationContractPreview(clubId: string, name?: string): Promise<Blob> {
  const { data } = await api.get(`/plan-activation/${clubId}/contract-preview`, {
    params: name ? { name } : undefined,
    responseType: "blob",
  });
  return data as Blob;
}

export async function acceptActivation(
  clubId: string,
  acceptedName: string
): Promise<{ ok: boolean; already_active: boolean; plan_code: string; contract: { id: string; number: string; title: string } | null }> {
  const { data } = await api.post(`/plan-activation/${clubId}/accept`, {
    accept: true,
    accepted_name: acceptedName,
  });
  return data;
}

export async function getClubContracts(clubId: string): Promise<ClubContract[]> {
  const { data } = await api.get<{ contracts: ClubContract[] }>(`/plan-activation/${clubId}/contracts`);
  return data.contracts || [];
}

export async function downloadClubContract(clubId: string, contractId: string): Promise<Blob> {
  const { data } = await api.get(`/plan-activation/${clubId}/contracts/${contractId}/pdf`, {
    responseType: "blob",
  });
  return data as Blob;
}

/** Cents to a readable price, in the currency the quote came back in. */
export function formatMoney(cents: number, currency = "EUR"): string {
  return `${(cents / 100).toFixed(2)} ${currency}`;
}
